import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import { type AssignedDocument, planDocumentsService } from '@/services/data/PlanDocumentsService';

export interface UsePlanDocumentsResult {
  documents: AssignedDocument[];
  isLoading: boolean;
  error: Error | null;
  assign: (args: { documentMessageId: string; documentSessionId: string }) => Promise<void>;
  unassign: (id: string) => Promise<void>;
  isAssigning: boolean;
  isUnassigning: boolean;
}

/**
 * Read + write hook for documents attached to a specific plan.
 *
 * Cache key: `['plan-documents', planId, userId]`. Invalidated on every
 * assign/unassign so the Plan Detail sidebar refreshes optimistically.
 */
export function usePlanDocuments(planId: string | null | undefined): UsePlanDocumentsResult {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['plan-documents', planId, userId],
    queryFn: () => {
      if (!planId || !userId) return [];
      return planDocumentsService.listByPlan(planId, userId);
    },
    enabled: !!planId && !!userId,
    staleTime: 30_000,
  });

  const assignMutation = useMutation({
    mutationFn: async (args: { documentMessageId: string; documentSessionId: string }) => {
      if (!planId || !userId) throw new Error('Plan or user missing');
      const res = await planDocumentsService.assign({
        planId,
        documentMessageId: args.documentMessageId,
        documentSessionId: args.documentSessionId,
        userId,
      });
      if (!res.ok) throw new Error(res.error);
      return res.row;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-documents', planId, userId] });
    },
  });

  const unassignMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!userId) throw new Error('User missing');
      const res = await planDocumentsService.unassign(id, userId);
      if (!res.ok) throw new Error(res.error);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-documents', planId, userId] });
    },
  });

  return {
    documents: data ?? [],
    isLoading,
    error: (error as Error | null) ?? null,
    assign: async (args) => {
      await assignMutation.mutateAsync(args);
    },
    unassign: async (id) => {
      await unassignMutation.mutateAsync(id);
    },
    isAssigning: assignMutation.isPending,
    isUnassigning: unassignMutation.isPending,
  };
}
